import { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import api from "@/lib/api";
import { toast } from "sonner";
import { Camera, Heart, Briefcase, User, ShieldCheck, CheckCircle2, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import ImageCropModal from "@/components/ImageCropModal";
import {
  normalizeHeic,
  isValidProfileType,
  fileToDataUrl,
  mapUploadError,
  MSG,
} from "@/lib/imageUpload";

const BASIC_FIELDS = [
  { k: "full_name", l: "Full name" },
  { k: "date_of_birth", l: "Date of birth", t: "date" },
  { k: "birth_time", l: "Time of birth (for Kundali)", t: "time" },
  { k: "birth_place", l: "Place of birth (for Kundali)" },
  { k: "gender", l: "Gender", opts: ["male", "female", "non-binary"] },
  { k: "city", l: "City" },
  { k: "state", l: "State" },
  { k: "country", l: "Country" },
  { k: "community", l: "Community" },
  { k: "mother_tongue", l: "Mother tongue" },
  { k: "height_cm", l: "Height (cm)", t: "number" },
];

// Lifestyle & Values (60% weight)
const LIFESTYLE_FIELDS = [
  { k: "religion", l: "Religion" },
  { k: "religious_views", l: "Religious views", opts: ["Practising", "Spiritual", "Cultural", "Non-practising"] },
  { k: "diet", l: "Diet", opts: ["Vegetarian", "Eggetarian", "Non-Vegetarian", "Vegan", "Jain"] },
  { k: "drinking", l: "Drinking", opts: ["Non-drinker", "Occasional", "Social", "Regular"] },
  { k: "smoking", l: "Smoking", opts: ["Non-smoker", "Occasional", "Regular"] },
  { k: "fitness", l: "Fitness & wellness", opts: ["Fitness-focused", "Balanced", "Sedentary"] },
  { k: "family_type", l: "Family type", opts: ["nuclear", "joint", "extended"] },
  { k: "family_values", l: "Family values", opts: ["traditional", "moderate", "modern"] },
];

// Professional & Educational (40% weight)
const PROFESSIONAL_FIELDS = [
  { k: "highest_degree_level", l: "Highest degree", opts: ["Diploma", "Bachelor's", "Professional", "Master's", "Doctorate", "PhD"] },
  { k: "education", l: "Education (institution / stream)" },
  { k: "industry", l: "Industry", opts: ["Technology", "Finance", "Healthcare", "Education", "Government", "Business", "Arts", "Manufacturing", "Retail", "Legal", "Other"] },
  { k: "profession", l: "Profession" },
  { k: "job_title", l: "Current job title" },
  { k: "company", l: "Company" },
  { k: "annual_income", l: "Annual income (INR)" },
];

function Section({ icon: Icon, title, subtitle, weight, fields, form, upd }) {
  return (
    <section className="mt-10">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-amber-50 border border-amber-200 grid place-items-center">
            <Icon size={16} className="text-amber-700" />
          </div>
          <div>
            <div className="font-serif-display text-xl text-[#1E1B4B]">{title}</div>
            <div className="text-xs text-stone-500">{subtitle}</div>
          </div>
        </div>
        {weight && (
          <div className="text-xs uppercase tracking-widest bg-amber-100 border border-amber-300 text-amber-800 rounded-full px-3 py-1">
            {weight} of match score
          </div>
        )}
      </div>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
        {fields.map((f) => (
          <div key={f.k}>
            <label className="text-xs uppercase tracking-widest text-stone-500">{f.l}</label>
            {f.opts ? (
              <select data-testid={`profile-${f.k}`} value={form[f.k] || ""} onChange={upd(f.k)}
                className="mt-2 w-full border border-stone-300 rounded-xl px-4 py-3 bg-white">
                <option value="">Select…</option>
                {f.opts.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input data-testid={`profile-${f.k}`} type={f.t || "text"} value={form[f.k] || ""} onChange={upd(f.k)}
                className="mt-2 w-full border border-stone-300 rounded-xl px-4 py-3 bg-white focus:border-[#1E1B4B]" />
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Profile() {
  const { user } = useAuth();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const kycVerified = user?.kyc_status === "verified";

  // Mount-only initial load. `api` is a stable module import; `setForm` is a stable React setter.
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/profile");
      setForm(data.profile || {});
    })();
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSelectPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (!isValidProfileType(file)) { toast.error(MSG.photoBadType); return; }
    setUploadSuccess(false);
    setPreparing(true);
    try {
      const normalized = await normalizeHeic(file); // iPhone HEIC -> JPG
      const dataUrl = await fileToDataUrl(normalized);
      setCropSrc(dataUrl);
    } catch {
      toast.error(MSG.processing);
    } finally {
      setPreparing(false);
    }
  };

  const onCropConfirm = async (croppedFile, err) => {
    setCropSrc(null);
    if (err || !croppedFile) { toast.error(MSG.processing); return; }
    setUploading(true);
    setUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", croppedFile);
      const { data } = await api.post("/upload/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const abs = data.url.startsWith("http") ? data.url : `${backendUrl}${data.url}`;
      setForm((f) => ({ ...f, photo_url: abs }));
      setUploadSuccess(true);
      toast.success("Photo uploaded successfully.");
    } catch (err2) {
      toast.error(mapUploadError(err2));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/profile", form);
      toast.success("Profile saved");
    } catch {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Nav />
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12">
        <div className="overline">Your Biodata</div>
        <h1 className="font-serif-display text-4xl text-[#1E1B4B] mt-2">Tell us about yourself.</h1>
        <p className="mt-3 text-stone-600 max-w-2xl">
          This informs your matches. Our engine weighs <strong>Lifestyle &amp; Values (60%)</strong> and{" "}
          <strong>Professional &amp; Educational (40%)</strong> — the more complete your profile, the sharper your matches.
        </p>

        {/* Photo upload */}
        <div className="mt-8 border border-stone-200 rounded-2xl bg-white p-6 flex items-center gap-6">
          <div className="relative w-20 h-20">
            <div className="w-20 h-20 rounded-full overflow-hidden border border-stone-200 grid place-items-center bg-stone-100">
              {form.photo_url ? (
                <img src={form.photo_url} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <Camera size={28} className="text-stone-400" />
              )}
            </div>
            {kycVerified && (
              <div
                data-testid="kyc-verified-badge"
                title="Identity verified"
                className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-emerald-500 border-2 border-white grid place-items-center shadow"
              >
                <ShieldCheck size={14} className="text-white" />
              </div>
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="overline">Profile photo</div>
              {kycVerified ? (
                <span data-testid="kyc-verified-pill" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-emerald-50 border border-emerald-300 text-emerald-800 rounded-full px-2 py-0.5">
                  <ShieldCheck size={10} /> KYC Verified
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-amber-50 border border-amber-300 text-amber-800 rounded-full px-2 py-0.5">
                  Pending verification
                </span>
              )}
            </div>
            <p className="text-sm text-stone-600 mt-1">Accepted formats: JPG, PNG, WEBP. Maximum size: 2 MB. iPhone photos are automatically converted.</p>
            <label className={`btn-pill mt-3 inline-flex items-center gap-2 text-sm bg-[#1E1B4B] text-white px-5 py-2.5 ${(uploading || preparing) ? "opacity-60 cursor-default" : "cursor-pointer"}`}>
              {preparing ? (<><Loader2 size={15} className="animate-spin" /> Preparing…</>)
                : uploading ? (<><Loader2 size={15} className="animate-spin" /> Uploading…</>)
                : "Choose photo"}
              <input data-testid="profile-photo-upload" type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" className="hidden" onChange={onSelectPhoto} disabled={uploading || preparing} />
            </label>

            {uploading && (
              <div data-testid="upload-progress" className="mt-3 max-w-xs">
                <div className="h-2 w-full rounded-full bg-stone-200 overflow-hidden">
                  <div className="h-full bg-[#1E1B4B] transition-all duration-200" style={{ width: `${uploadProgress}%` }} />
                </div>
                <div className="text-xs text-stone-500 mt-1">Uploading… {uploadProgress}%</div>
              </div>
            )}
            {uploadSuccess && !uploading && (
              <div data-testid="upload-success" className="mt-3 inline-flex items-center gap-1.5 text-sm text-emerald-700">
                <CheckCircle2 size={16} className="text-emerald-600" /> Photo uploaded successfully.
              </div>
            )}
          </div>
        </div>

        {cropSrc && (
          <ImageCropModal
            imageSrc={cropSrc}
            onConfirm={onCropConfirm}
            onChooseAgain={() => { setCropSrc(null); document.querySelector('[data-testid="profile-photo-upload"]')?.click(); }}
            onCancel={() => setCropSrc(null)}
          />
        )}

        <form onSubmit={save}>
          <Section icon={User} title="Basic" subtitle="Identity, birth details, location" fields={BASIC_FIELDS} form={form} upd={upd} />
          <Section icon={Heart} title="Lifestyle & Values" subtitle="Diet · religion · habits · family orientation" weight="60%" fields={LIFESTYLE_FIELDS} form={form} upd={upd} />
          <Section icon={Briefcase} title="Professional & Educational" subtitle="Industry · degree · role" weight="40%" fields={PROFESSIONAL_FIELDS} form={form} upd={upd} />

          <section className="mt-10">
            <div className="grid grid-cols-1 gap-5">
              <div>
                <label className="text-xs uppercase tracking-widest text-stone-500">About me</label>
                <textarea data-testid="profile-about_me" rows={4} value={form.about_me || ""} onChange={upd("about_me")}
                  className="mt-2 w-full border border-stone-300 rounded-xl px-4 py-3 bg-white" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-stone-500">Partner expectations</label>
                <textarea data-testid="profile-partner_expectations" rows={3} value={form.partner_expectations || ""} onChange={upd("partner_expectations")}
                  className="mt-2 w-full border border-stone-300 rounded-xl px-4 py-3 bg-white" />
              </div>
            </div>
          </section>

          <div className="mt-10">
            <button data-testid="profile-save-btn" disabled={saving}
              className="btn-pill text-white px-8 py-3" style={{ background: "#1E1B4B" }}>
              {saving ? "Saving…" : "Save profile"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
